'use client';
import { genericMemo, toBoolean } from '@mnd-frontend/ts';
import { GREY_400, GREY_600 } from '@mnd-frontend/ui/shared/tokens';
import { localizeValue, scaledRemCalc } from '@mnd-frontend/utils';
import { scaleBand } from 'd3-scale';
import React, { type ReactNode } from 'react';
import styled from 'styled-components';
import { Legend, PositionRelative, SvgChart } from '../lib/components';
import { Grid } from '../lib/components/Graph';
import { BottomMonths, BottomStrict } from '../lib/components/Graph/Axes/Bottom';
import Left from '../lib/components/Graph/Axes/Left';
import { GraphWrapper } from '../lib/components/GraphWrapper';
import { ContextProvider } from '../lib/context';
import { useScaleBandX, useScaleLinearY } from '../lib/hooks/d3';
import { useColorAndId, useFilterUnselected } from '../lib/hooks/data';
import { max } from '../lib/utils';
import type { DataPoint, Scale } from '../types';

const formatValue = (value: number) => localizeValue(value, { abbrev: true });

const StyledText = styled.text<{ $faded?: boolean }>`
  fill: #000;
  text-anchor: middle;
  font-size: ${scaledRemCalc(14)};
  ${props =>
    props.$faded &&
    `
    fill: ${GREY_600};
  `}
`;
const ZeroLine = styled.line`
  stroke: ${GREY_400};
  stroke-width: 1px;
`;

type ExtendedDataPoint = DataPoint & {
  group?: number | string;
  month?: number;
};

const Graph = <T extends ExtendedDataPoint>({
  data,
  mappings,
  percent = false,
  showMonths,
  onBarClick,
}: {
  data: T[];
  mappings: {
    name: string | ReactNode;
    dataKey: keyof Omit<T, 'name'>;
    color: string;
    id: string;
    detailsDataKey?: keyof T;
  }[];
  percent?: boolean;
  showMonths?: boolean;
  onBarClick?: (item: any) => void;
}) => {
  const filteredMappings = useFilterUnselected(mappings);
  const maxValue = Math.ceil(max(data, d => max(filteredMappings, m => d[m.dataKey])) || 0);

  const scaleX = useScaleBandX(data, 0.3, 0.2);
  const scaleY = useScaleLinearY(0, maxValue);
  const [x0, x1] = scaleX.range();
  const yZero = scaleY(0);
  const [y0] = scaleY.range();

  return (
    <PositionRelative>
      <SvgChart>
        <Grid scaleX={scaleX} scaleY={scaleY} />
        <Bars
          mappings={filteredMappings}
          data={data}
          scaleY={scaleY}
          drawXAxis
          percent={percent}
          onBarClick={onBarClick}
        />
        <Left scale={scaleY} x={x0} includeFirstTick percent={percent} />
        <ZeroLine y1={yZero} y2={yZero} x1={x0} x2={x1} />
        <ZeroLine y1={y0} y2={y0} x1={x0} x2={x1} />
        {showMonths && <BottomMonths data={data} y={y0} extraPadding={24} />}
      </SvgChart>
    </PositionRelative>
  );
};
const Bars = <T extends ExtendedDataPoint>({
  data,
  mappings,
  scaleY,
  drawXAxis,
  percent = false,
  onBarClick,
}: {
  drawXAxis?: boolean;
  scaleY: Scale;
  data: T[];
  mappings: {
    name: string | ReactNode;
    dataKey: keyof Omit<T, 'name'>;
    color: string;
    id: string;
    detailsDataKey?: keyof T;
  }[];
  percent?: boolean;
  onBarClick?: (item: any) => void;
}) => {
  const [y0] = scaleY.range();

  const renderData = data.flatMap((item, i) => {
    const row = mappings.map(mapping => {
      return {
        id: mapping.id + '_' + i,
        dataIndex: i,
        color: mapping.color,
        value: Number(item[mapping.dataKey]),
        name: item.name,
        group: item.group,
        dataPeriod: mapping.detailsDataKey,
        details:
          typeof mapping.detailsDataKey === 'string'
            ? (item[mapping.detailsDataKey] as unknown)
            : undefined,
      };
    });
    return row;
  });

  const uniqueGroupIds = [...new Set(renderData.map(i => i.group))].filter(toBoolean);
  const hasGroups = uniqueGroupIds.length > 1;

  const groupedData = hasGroups
    ? uniqueGroupIds.map(id => {
        return renderData.filter(i => i.group === id);
      })
    : [renderData];

  const scaleX = useScaleBandX(groupedData, hasGroups ? 0.1 : 0, hasGroups ? 0.1 : 0.01);
  const outerWidth = scaleX.bandwidth();
  const sets = mappings.length;
  const mid = (sets - 1) / 2;

  return (
    <>
      {groupedData.map((group, i) => {
        const outerX = scaleX(i.toString()) || 0;
        const innerScale = scaleBand()
          .domain(group.map((_d, i) => i.toString()))
          .range([outerX, outerWidth + outerX])
          .paddingInner(0.1)
          .paddingOuter(0);

        const xDomains = group
          .filter((_d, i) => i % mappings.length === 0)
          .map(({ name }) => ({ name }));
        const xAxisScale = scaleBand()
          .domain(xDomains.map((_d, i) => i.toString()))
          .range([outerX, outerWidth + outerX])
          .paddingInner(0.1)
          .paddingOuter(0);

        const width = innerScale.bandwidth();
        const gap = width * innerScale.paddingInner();

        return (
          <React.Fragment key={i}>
            {drawXAxis && <BottomStrict scale={xAxisScale as any} y={y0} data={xDomains} />}
            {group.map((item, j) => {
              const { id, color, value } = item;
              const cPos = j % sets;
              const offsetP = cPos - mid;
              const offset = offsetP * gap;
              const x = (innerScale(j.toString()) || 0) - offset;
              const y = scaleY(value) || 0;
              const height = (scaleY(0) || 0) - y;
              const textY = y - 8;

              const isInteractive = Boolean(onBarClick && item?.dataPeriod);
              const interactiveProps = isInteractive
                ? {
                    role: 'button' as const,
                    tabIndex: 0,
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.code === 'Enter' || e.code === 'Space') {
                        e.stopPropagation();
                        e.preventDefault();
                        onBarClick?.({
                          details: item.details,
                          dataIndex: item.dataIndex,
                          dataPeriod: item.dataPeriod,
                        });
                      }
                    },
                    onClick: () =>
                      onBarClick?.({
                        details: item.details,
                        dataIndex: item.dataIndex,
                        dataPeriod: item.dataPeriod,
                      }),
                  }
                : ({ role: 'img' } as const);
              return (
                <React.Fragment key={id}>
                  <StyledText x={x + width * 0.5} y={textY} width={width} $faded={value === 0}>
                    {formatValue(value)}
                    {percent ? '%' : ''}
                  </StyledText>
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={color}
                    cursor={isInteractive ? 'pointer' : undefined}
                    {...interactiveProps}
                  />
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};
export const BarChartMulti = genericMemo(
  <T extends DataPoint>({
    data,
    height = 320,
    mappings,
    legend = true,

    percent = false,
    showMonths,
    onBarClick,
  }: {
    data: T[];
    height?: number;
    mappings: {
      name: string | ReactNode;
      dataKey: Extract<keyof Omit<T, 'name'>, string>;
      color?: string;
      negative?: boolean;
      detailsDataKey?: keyof T;
    }[];
    legend?: boolean;

    percent?: boolean;
    showMonths?: boolean;
    onBarClick?: (item: any) => void;
  }) => {
    const coloredMappings = useColorAndId(mappings);

    return (
      <ContextProvider minWidth={200} height={height} selectToggle hideSelected>
        <GraphWrapper>
          {legend && <Legend items={coloredMappings} />}
          <Graph
            mappings={coloredMappings}
            data={data}
            percent={percent}
            showMonths={showMonths}
            onBarClick={onBarClick}
          />
        </GraphWrapper>
      </ContextProvider>
    );
  },
);
